import Link from '@mui/material/Link';
import type { TypographyVariant } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { MouseEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
	Address,
	AddressProps
} from '@/components/Cards/AddressCard/Address/Address';
import { Separated } from '@/components/Separated/Separated';
import type { Hcphco } from '@/core/graphql/generated/types';
import { mergeSx } from '@/utils/mergeSx';

import { useProfileMetaData } from '../HcpCard/_hooks/useProfileMetaData';

import { affiliationAddressStyles as sx } from './AffiliationAddress.styles';

export type AffiliationAddressType = 'link' | 'inLink' | 'text';

type Props = {
	primaryAffiliation?: Hcphco | null;
	addressView?: AddressProps['view'];
	type?: AffiliationAddressType;
	variant?: TypographyVariant;
	layout?: 'cut' | 'wrap';
	onNavigate?: () => void;
};

export const AffiliationAddress = ({
	primaryAffiliation,
	addressView = 'basic',
	type = 'link',
	variant = 'body1',
	layout = 'wrap',
	onNavigate
}: Props) => {
	const navigate = useNavigate();
	const {
		profileLink: affiliationLink,
		getProfileName,
		primaryAddress
	} = useProfileMetaData(primaryAffiliation ?? null);

	if (!primaryAffiliation && !primaryAddress) return null;

	const handleAffiliationClick = (e: MouseEvent) => {
		e.preventDefault();
		onNavigate?.();
		navigate(affiliationLink);
	};

	const { name: affiliationName } = getProfileName({
		withInternationalName: true
	});

	const affiliationNode = primaryAffiliation ? (
		type === 'text' ? (
			<Typography component='span' variant={variant}>
				{affiliationName}
			</Typography>
		) : type === 'inLink' ? (
			<Typography
				component='span'
				variant={variant}
				onClick={handleAffiliationClick}
				sx={mergeSx(sx.affiliationNameColor, sx.affiliationClickTarget)}
			>
				{affiliationName}
			</Typography>
		) : (
			<Link
				component={RouterLink}
				to={affiliationLink}
				underline='none'
				variant={variant}
				sx={sx.linkWeight}
				onClick={onNavigate}
			>
				{affiliationName}
			</Link>
		)
	) : undefined;

	return (
		<Separated
			layout={layout}
			variant={variant}
			left={affiliationNode}
			right={
				primaryAddress ? (
					<Address
						address={primaryAddress}
						view={addressView}
						inline
						customSx={sx.addressInherit}
					/>
				) : undefined
			}
		/>
	);
};
